import { useState, type CSSProperties, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { IconLoader2 } from "@tabler/icons-react";
import {
  resolveBodyFontFamily,
  resolveButtonThemeForType,
} from "../theme/themeValueResolver";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { safeHref } from "../lib/safe-href";
import { navigateOrScroll } from "../lib/linkTarget";
import {
  type Optional,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  toCssLength,
  useAppBarTransparent,
  useDataInputNavigation,
  useSubmitState,
} from "./shared";

export function Button({
  text,
  defaultText,
  submittedText,
  isSubmitted = false,
  enableSubmittedState = false,
  linkType = "internal",
  linkUrl,
  type = "primary",
  variant,
  buttonType = "button",
  size = "medium",
  underline = false,
  border = false,
  borderWidth,
  borderStyle,
  borderColor,
  backgroundColor,
  textColor,
  widthMode = "content",
  rounded,
  hoverRounded,
  fontType,
  margin,
  disabled,
  loading,
  isVisible = true,
  navigationRole,
  onClickAction,
  bgHoverColor,
  textHoverColor,
  borderHoverColor,
  transparentBackgroundColor,
  transparentTextColor,
  transparentBorderColor,
}: {
  text?: LangText;
  defaultText?: Optional<string>;
  submittedText?: LangText;
  isSubmitted?: Optional<boolean>;
  enableSubmittedState?: Optional<boolean>;
  linkType?: Optional<"internal" | "external">;
  linkUrl?: Optional<string>;
  type?: Optional<"primary" | "secondary" | "tertiary">;
  // Visual variant — twin of the editor Button. undefined/"solid" = current
  // rendering (byte-identical); pill = rounded-full; ghost = transparent bg +
  // 1px currentColor border; elevated = shadow + motion-safe hover lift.
  variant?: Optional<"solid" | "pill" | "ghost" | "elevated">;
  buttonType?: Optional<"button" | "submit" | "companion" | "slotting">;
  size?: Optional<"xlarge" | "large" | "medium" | "small" | "xsmall">;
  underline?: Optional<boolean>;
  border?: Optional<boolean>;
  borderWidth?: Optional<string>;
  borderStyle?: Optional<string>;
  borderColor?: Optional<string>;
  backgroundColor?: Optional<string>;
  textColor?: Optional<string>;
  widthMode?: Optional<"full" | "content">;
  rounded?: Optional<string>;
  hoverRounded?: Optional<string>;
  fontType?: Optional<string>;
  margin?: Optional<string>;
  disabled?: Optional<boolean>;
  loading?: Optional<boolean>;
  isVisible?: Optional<boolean>;
  navigationRole?: Optional<"back" | "next">;
  onClickAction?: Optional<
    (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>
  >;
  bgHoverColor?: Optional<string>;
  textHoverColor?: Optional<string>;
  borderHoverColor?: Optional<string>;
  transparentBackgroundColor?: Optional<string>;
  transparentTextColor?: Optional<string>;
  transparentBorderColor?: Optional<string>;
}) {
  const navigate = useNavigate();
  const { buttonsTheme, currentColorPalette, currentRounding, currentFonts } =
    useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const nav = useDataInputNavigation();
  const [isHovered, setIsHovered] = useState(false);

  // Read BEFORE the guard below. `useContext` happens not to occupy a slot in
  // React's hook list, so calling it after an early return does not actually
  // throw — but it still violates rules-of-hooks, and relying on that
  // implementation detail is not a contract worth depending on.
  const isTransparentActive = useAppBarTransparent();
  // Audit 2026-09-23 (4b): the owning form's live state beats the stored
  // `loading` / `isSubmitted` seeds (see SubmitStateContext).
  const submitState = useSubmitState();
  const effectiveLoading = submitState ? submitState.loading : Boolean(loading);

  // Inside DataInput, the parent wrapper div controls visibility via
  // display:none. Ignore serialized isVisible to avoid stale editor state.
  if (!isVisible && !nav) return null;

  const activeTheme = resolveButtonThemeForType({
    type: type === "secondary" ? "secondary" : "primary",
    buttonsTheme,
    palette: currentColorPalette,
    currentRounding,
  });
  const resolvedRounded =
    rounded ?? activeTheme.borderRadius ?? currentRounding;
  const resolvedHoverRounded =
    hoverRounded ?? activeTheme.hoverBorderRadius ?? resolvedRounded;
  // pill variant forces rounded-full regardless of theme/hover rounding
  const activeRounded =
    variant === "pill"
      ? "rounded-full"
      : isHovered
        ? resolvedHoverRounded
        : resolvedRounded;
  const roundedClasses = extractUtilityClasses(activeRounded, "rounded");
  const resolvedBackground = isTransparentActive && transparentBackgroundColor
    ? transparentBackgroundColor
    : isHovered
      ? (bgHoverColor ?? activeTheme.bgHoverColor)
      : (backgroundColor ?? activeTheme.bgColor);
  const resolvedTextColor = isTransparentActive && transparentTextColor
    ? transparentTextColor
    : isHovered
      ? (textHoverColor ?? activeTheme.fontHoverColor)
      : (textColor ?? activeTheme.fontColor);
  const resolvedBorderColor = isTransparentActive && transparentBorderColor
    ? transparentBorderColor
    : isHovered
      ? (borderHoverColor ?? activeTheme.borderHoverColor)
      : (borderColor ?? activeTheme.borderColor);

  const sizeClasses: Record<string, string> = {
    xlarge: "px-8 py-4",
    large: "px-6 py-3",
    medium: "px-4 py-2",
    small: "px-3 py-1.5",
    xsmall: "px-2 py-1",
  };
  const widthClass = widthMode === "full" ? "w-full" : "w-max";
  // ghost: transparent background + 1px border in the text color. Spread LAST
  // so it wins over the resolved background/border colors — mirrors the editor
  // Button's variantStyleOverrides. undefined/"solid" adds nothing → existing
  // buttons render byte-identical.
  const variantStyleOverrides: CSSProperties =
    variant === "ghost"
      ? { backgroundColor: "transparent", border: "1px solid currentColor" }
      : {};
  const resolvedText = resolveText(text, currentLang, primaryLanguage);
  const resolvedSubmitted = resolveText(submittedText, currentLang, primaryLanguage);
  const baseText =
    resolvedText || (buttonType === "submit" ? defaultText : undefined);
  const submitted = submitState
    ? submitState.isSubmitted
    : Boolean(isSubmitted && enableSubmittedState);
  const effectiveText =
    submitted && resolvedSubmitted ? resolvedSubmitted : baseText;

  return (
    <button
      className={classNames(
        "flex h-min flex-row justify-center items-center",
        sizeClasses[size],
        roundedClasses,
        getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid"),
        underline ? "underline" : undefined,
        // elevated: soft shadow + motion-safe hover lift (editor parity)
        variant === "elevated"
          ? "shadow-md hover:shadow-lg motion-safe:hover:-translate-y-0.5"
          : undefined,
        disabled ? "opacity-50" : undefined,
        margin,
        widthClass,
      )}
      type={
        buttonType === "companion" || buttonType === "slotting"
          ? "button"
          : buttonType
      }
      style={{
        borderRadius: roundedClasses ? undefined : toCssLength(activeRounded),
        borderColor: border ? resolvedBorderColor : undefined,
        background: resolvedBackground,
        color: resolvedTextColor,
        fontFamily: resolveBodyFontFamily({
          override: fontType,
          fonts: currentFonts,
        }),
        // Match the editor's button typography: theme font-size + the
        // `body-*-medium` weight (500) and 1.5 line-height the editor applies
        // via class. The runtime previously applied none of these, so live
        // buttons rendered at the inherited size/weight and ignored a
        // theme-customized button font size.
        fontSize: isHovered ? activeTheme.fontHoverSize : activeTheme.fontSize,
        fontWeight: 500,
        lineHeight: 1.5,
        transition: "all 0.3s ease",
        cursor: disabled ? "not-allowed" : "pointer",
        ...variantStyleOverrides,
      }}
      disabled={disabled || effectiveLoading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        if (buttonType === "submit") return;

        if (onClickAction) {
          event.preventDefault();
          void onClickAction(event);
          return;
        }

        if (navigationRole && nav) {
          event.preventDefault();
          if (navigationRole === "back") nav.goBack();
          if (navigationRole === "next") nav.goNext();
          return;
        }

        if (!linkUrl) return;

        if (linkType === "external") {
          const safe = safeHref(linkUrl);
          if (safe !== "#") window.open(safe, "_blank", "noopener,noreferrer");
          return;
        }

        event.preventDefault();
        navigateOrScroll(navigate, linkUrl);
      }}
    >
      {effectiveLoading ? (
        <IconLoader2 className="animate-spin mr-2" size={20} />
      ) : null}
      {effectiveText}
    </button>
  );
}
